import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { brandInitials } from "@/hooks/use-brand-branding";

interface BrandLogoProps {
  src?: string | null;
  name?: string | null;
  size?: number;
  className?: string;
  rounded?: "full" | "lg" | "md";
  bg?: string;
  fg?: string;
  border?: boolean;
  primaryColor?: string | null;
}

// Cache detection per image src so we don't recompute.
// "opaque"   = logo has its own background (no transparent pixels) → blend with white
// "light"    = transparent logo, dark artwork → white backdrop
// "dark"     = transparent logo, light/white artwork → navy backdrop so it stays visible
type LogoKind = "opaque" | "light" | "dark";
const lumaCache = new Map<string, LogoKind>();

function detectLogoBg(src: string): Promise<LogoKind> {
  if (lumaCache.has(src)) return Promise.resolve(lumaCache.get(src)!);
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const w = 24, h = 24;
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) { lumaCache.set(src, "opaque"); return resolve("opaque"); }
        ctx.drawImage(img, 0, 0, w, h);
        const { data } = ctx.getImageData(0, 0, w, h);
        let total = 0, count = 0, transparent = 0;
        const totalPx = (data.length / 4);
        for (let i = 0; i < data.length; i += 4) {
          const a = data[i + 3];
          if (a < 32) { transparent++; continue; }
          const r = data[i], g = data[i + 1], b = data[i + 2];
          const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
          total += lum; count++;
        }
        // If less than 5% transparent pixels, treat as opaque (logo has its own bg).
        if (transparent / totalPx < 0.05) {
          lumaCache.set(src, "opaque");
          return resolve("opaque");
        }
        if (count === 0) { lumaCache.set(src, "light"); return resolve("light"); }
        const avg = total / count;
        const result: LogoKind = avg > 0.82 ? "dark" : "light";
        lumaCache.set(src, result);
        resolve(result);
      } catch {
        lumaCache.set(src, "opaque");
        resolve("opaque");
      }
    };
    img.onerror = () => { lumaCache.set(src, "opaque"); resolve("opaque"); };
    img.src = src;
  });
}

const BrandLogo = ({
  src,
  name,
  size = 32,
  className,
  rounded = "lg",
  bg = "#EEEDFE",
  fg = "#3C3489",
  border = true,
  primaryColor: _primaryColor = null,
}: BrandLogoProps) => {
  const [errored, setErrored] = useState(false);
  const [detected, setDetected] = useState<LogoKind | null>(
    src && lumaCache.has(src) ? lumaCache.get(src)! : null
  );

  useEffect(() => {
    setErrored(false);
    if (!src) { setDetected(null); return; }
    if (lumaCache.has(src)) { setDetected(lumaCache.get(src)!); return; }
    let alive = true;
    detectLogoBg(src).then((r) => { if (alive) setDetected(r); });
    return () => { alive = false; };
  }, [src]);

  const showImg = src && !errored;
  const radius =
    rounded === "full" ? "9999px" : rounded === "lg" ? "10px" : "6px";

  // Opaque & transparent-dark artwork → white pad.
  // Transparent + light/white artwork → one fixed navy for every brand (scraped colors can be stale).
  const imgBg = detected === "dark" ? "#0E1626" : "#fff";



  return (
    <div
      className={cn(
        "shrink-0 inline-flex items-center justify-center overflow-hidden",
        border && "ring-1 ring-black/10",
        className
      )}
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        background: showImg ? imgBg : bg,
      }}
    >
      {showImg ? (
        <img
          src={src!}
          alt={name || "Brand"}
          onError={() => setErrored(true)}
          className="w-full h-full object-contain"
          style={{
            padding:
              detected === "opaque"
                ? 0
                : Math.max(2, Math.round(size * 0.1)),
          }}
        />

      ) : (

        <span
          style={{
            color: fg,
            fontSize: Math.max(10, Math.round(size * 0.38)),
            fontWeight: 600,
            letterSpacing: 0.2,
          }}
        >
          {brandInitials(name)}
        </span>
      )}
    </div>
  );
};

export default BrandLogo;
